import { gql } from '@apollo/client';
import { jsonToGraphQLQuery, VariableType } from 'json-to-graphql-query';
import { merge, set } from 'lodash';
import {
  CONTACTS_FIELD_ENTITY,
  NO_PARENT_FIELD_ENTITY,
} from 'src/views/Coversheet/constants/coverSheet';
import { FIELD_NAME_INDEXABLE_INDICATOR } from 'src/views/Coversheet/utils/indexifyValues';
import type { CoverSheetConfig, Field } from '../configurations/types';

const getFieldQueryJson = (
  name: string,
  _type: string,
  entity?: Field['entity'],
  entityType?: Field['entityType'],
): Record<string, boolean> => {
  let fieldsJson: Record<string, boolean> = {};
  // entity on field implies it should be queryable
  if (!entity) return fieldsJson;

  // replace indexable indicator of field names e.g. agent.x.firstName
  const fieldNameParts = name
    .replace(FIELD_NAME_INDEXABLE_INDICATOR, '.')
    .split('.');
  if (entity !== NO_PARENT_FIELD_ENTITY && fieldNameParts[0] !== entity)
    fieldNameParts.unshift(entity);
  const isContactType = entity === CONTACTS_FIELD_ENTITY;

  // add entity if it's not part of the name already
  if (entity !== NO_PARENT_FIELD_ENTITY && fieldNameParts[0] !== entity)
    fieldNameParts.unshift(entity);

  /*
    contacts are a special case. The interaction has a single list of all
    the contacts and each has a type. For easier form management it will
    be ideal to have the interaction have a contacts property with lists
    of contacts grouped by type e.g.
    { contacts: { agent: [], client: [] } }
    With this in mind the field names will look like
    contacts.agent.x.firstName so we want to remove entityType e.g.
    "agent" when finding fields to query
  */
  if (isContactType) {
    const entityTypeIndex = fieldNameParts.findIndex(
      (item: string) => item === entityType,
    );
    if (entityTypeIndex > -1) {
      // manually add id/order/type to field contacts with entityType
      fieldsJson = set(fieldsJson, [entity, 'id'], true);
      fieldsJson = set(fieldsJson, [entity, 'order'], true);
      fieldsJson = set(fieldsJson, [entity, 'type'], true);
      fieldsJson = set(fieldsJson, [entity, 'googleContactId'], true);
      fieldsJson = set(fieldsJson, [entity, 'contactSource'], true);
      fieldNameParts.splice(entityTypeIndex, 1);
    }
  }

  return set(fieldsJson, fieldNameParts, true);
};

export const reduceFieldNamesToQueryJson = (
  coverSheetConfig: CoverSheetConfig,
) => {
  let fieldsJson: Record<string, boolean> = {
    id: true,
    isIntermediary: true,
    owner: true,
    teamId: true,
    ownerUserIdentityId: true,
    teamIdentityId: true,
  };
  coverSheetConfig?.sections.forEach((section) => {
    section.cards.forEach((card) => {
      card.fields.forEach(({ entity, entityType, name, customField, type }) => {
        fieldsJson = merge(
          fieldsJson,
          getFieldQueryJson(name, type, entity, entityType),
        );
        if (customField)
          fieldsJson = merge(
            fieldsJson,
            getFieldQueryJson(customField.name, type, entity, entityType),
          );
      });
    });
  });

  return fieldsJson;
};

export const generateCoverSheetQuery = (
  coverSheetConfig?: CoverSheetConfig | null,
) => {
  const fieldsJson = coverSheetConfig
    ? reduceFieldNamesToQueryJson(coverSheetConfig)
    : { id: true };
  const queryJson = {
    query: {
      __name: 'CoverSheet',
      __variables: {
        interactionId: 'ID!',
        interactionType: 'InteractionType!',
      },
      coverSheet: {
        __args: {
          input: {
            interactionId: new VariableType('interactionId'),
            interactionType: new VariableType('interactionType'),
          },
        },
        ...fieldsJson,
      },
    },
  };
  const queryString = jsonToGraphQLQuery(queryJson, { pretty: true });
  return gql`
    ${queryString}
  `;
};
